/**
 * Centralized configuration service for frontend
 * Provides typed, validated access to environment variables
 * 
 * @deprecated Use Config from '../config' instead
 */

import { Config, isViteDev } from '../config';

export interface AppConfig {
  apiBaseUrl: string;
  environment: string;
  authMode: 'production' | 'development' | 'demo';
  azureAd: {
    clientId: string;
    tenantId: string;
    redirectUri: string;
  };
  features: {
    chatbot: boolean;
    analytics: boolean;
  };
  security: {
    enforceHttps: boolean;
  };
  external: {
    contactUrl: string;
    supportEmail: string;
    documentationUrl: string;
  };
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableApiLogging: boolean;
    enablePerformanceMonitoring: boolean;
  };
}

class ConfigurationService {
  private config: AppConfig;

  constructor() {
    this.config = this.loadConfiguration();
    this.validate();
  }

  private loadConfiguration(): AppConfig {
    // Derive auth mode from Config
    const authMode = this.deriveAuthMode();

    return {
      apiBaseUrl: Config.api.baseUrl,
      environment: Config.environment,
      authMode,
      azureAd: {
        clientId: Config.auth.azure.clientId,
        tenantId: Config.auth.azure.tenantId,
        redirectUri: Config.auth.azure.redirectUri,
      },
      features: {
        chatbot: Config.features.chatbot,
        analytics: Config.features.analytics,
      },
      security: {
        enforceHttps: Config.security.enforceHttps,
      },
      external: {
        contactUrl: Config.external.contactUrl,
        supportEmail: Config.external.supportEmail,
        documentationUrl: Config.external.documentationUrl,
      },
      logging: {
        level: Config.logging.level,
        enableApiLogging: Config.logging.enableApiLogging,
        enablePerformanceMonitoring: Config.logging.enablePerformanceMonitoring,
      },
    };
  }

  /**
   * Consolidate conflicting auth mode flags into single source of truth
   */
  private deriveAuthMode(): 'production' | 'development' | 'demo' {
    if (Config.auth.demoMode) {
      return 'demo';
    }

    if (!Config.auth.enabled || Config.auth.localDev) {
      return 'development';
    }

    return 'production';
  }

  /**
   * Validate critical configuration at startup
   */
  private validate(): void {
    const errors: string[] = [];

    if (!this.config.apiBaseUrl) {
      errors.push('VITE_API_BASE_URL is required but not configured');
    }

    if (this.config.authMode === 'production') {
      if (!this.config.azureAd.clientId) {
        errors.push('VITE_AZURE_CLIENT_ID is required for production auth mode');
      }
      if (!this.config.azureAd.tenantId) {
        errors.push('VITE_AZURE_TENANT_ID is required for production auth mode');
      }
    }

    if (errors.length > 0) {
      const errorMessage = `Configuration validation failed:\n${errors.join('\n')}`;
      console.error(errorMessage);
      
      // In development, log error but don't crash
      if (isViteDev) {
        console.warn('Running with invalid configuration in development mode');
      } else {
        // In production, fail fast
        throw new Error(errorMessage);
      }
    }
  }

  /**
   * Get the complete configuration object
   */
  public get(): AppConfig {
    return this.config;
  }

  /**
   * Get a specific configuration value
   */
  public getValue<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.config[key];
  }
}

// Export singleton instance
export const configService = new ConfigurationService();